<template>
  <div class="music-player" v-if="playlist.length > 0">
    <!-- playlist block -->
    <div
      v-if="playlist.length && showPlaylist"
      class="playlist"
      :class="showPlaylist ? 'show' : 'hide'"
    >
      <v-sheet class="wrap">
        <div
          class="song-wrap"
          v-for="(audio, index) in playlist"
          @click="playSelectAudio(index)"
          :key="index"
        >
          <div
            class="song-details"
            style="display: flex; align-content: space-between"
          >
            <div>
              <span class="play">
                <i
                  class="mdi mdi-pause"
                  v-if="isPlaying && currentAudioIndex === index"
                  @click="pause"
                ></i>
                <i
                  class="mdi mdi-play"
                  v-else
                  @click="playSelectAudio(index)"
                ></i>
              </span>
              {{ index + 1 }}
            </div>
            <div>
              <span>
                <img
                  :style="{filter: isDark ? '' : 'brightness(0)'}"
                  class="music-bars-gif"
                  src="../../assets/animated-sound-bars.gif"
                  v-if="isPlaying && currentAudioIndex === index"
                />
              </span>
            </div>
          </div>
        </div>
      </v-sheet>
    </div>

    <v-sheet class="player" id="player">
      <div class="container py-0">
        <div class="main-controls">

          <div class="controls">
            <v-btn
              icon
              @click="skip('backward')"
            ><v-icon>mdi-skip-previous</v-icon></v-btn>

            <v-btn
              icon
              x-large
              outlined
              color="green"
              @click="changeIsPlaying()"
            >
              <v-icon v-if="!isPlaying">mdi-play</v-icon>
              <v-icon v-else>mdi-pause</v-icon>
            </v-btn>

            <v-btn
              icon
              @click="skip('forward')"
            ><v-icon>mdi-skip-next</v-icon></v-btn>

            <v-menu top offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>{{ getVolumeIcon }}</v-icon>
                </v-btn>
              </template>

              <v-card>
                <v-card-text style="width: 300px;">
                  <v-slider
                    v-model="volume"
                    :prepend-icon="getVolumeIcon"
                    hide-details
                    dense
                    min="0"
                    max="1"
                    step="0.05"
                    @input="changeVolume"
                  ></v-slider>
                </v-card-text>
              </v-card>
            </v-menu>
          </div>

          <div class="seek">
            <div class="title-and-time">
              <div class="seek_title">
                {{ currentAudioIndex + 1 }}
              </div>

              <div class="seek_speed">
                <v-chip
                  v-for="(speedVar, index) in [1, 1.25, 1.5, 1.75, 2]"
                  :key="index"
                  class="speed-option mr-2"
                  x-small
                  outlined
                  :color="speedVar === speed ? 'green' : null"
                  @click="changeSpeed(speedVar)"
                >
                  x{{ speedVar }}
                </v-chip>
              </div>

              <div class="seek_time">
                {{ formatTime(currentSeconds) }} /
                {{ formatTime(durationSeconds) }}
              </div>
            </div>

            <div class="progress-container">
              <div class="progress" id="progress" ref="progress">
                <div
                  class="progress-handle"
                  :style="{ left: progressPercentageValue }"
                ></div>
                <div class="transparent-seeker-layer" @click="seek"></div>
                <div
                  class="bar"
                  :style="{ width: progressPercentageValue }"
                ></div>

                <div
                  v-for="(phrase, index) in phrases"
                  :class="{
                    'agent-speech-bar': (!phrase.is_system && phrase.is_client === false),
                    'client-speech-bar': (!phrase.is_system && phrase.is_client)
                  }"
                  :style="getPhraseStyle(phrase)"
                  :key="index"
                ></div>

                <!-- Холды -->
                <div
                  v-for="(hold, index) in call.holds"
                  :key="`hold_${index}`"
                  style="background-color: #9820f3"
                  class="agent-speech-bar"
                  :style="getPhraseStyle(hold)"
                  title="удержание"
                ></div>
                <div
                  v-for="(transfer, index) in call.transfers"
                  :key="`transfer_${index}`"
                  style="background-color: #ff9800"
                  class="agent-speech-bar"
                  :style="getPhraseStyle(transfer)"
                  title="перевод"
                ></div>
              </div>
            </div>
          </div>

          <div class="extra-controls">

            <div v-if="call.can_download_call">
              <v-tooltip top>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    small
                    :href="currentAudio.src"
                    :download="getFilename()"
                    v-on="on"
                  >
                    <v-icon>mdi-download</v-icon>
                  </v-btn>
                </template>
                <span>Скачать аудио</span>
              </v-tooltip>
            </div>

            <div v-if="playlist.length > 1" class="playlist-icon">
              <v-tooltip top>
                <template #activator="{ on }">
                  <v-btn
                    icon
                    small
                    v-on="on"
                    @click="toggleShowPlaylist"
                  >
                    <v-icon>mdi-playlist-play</v-icon>
                  </v-btn>
                </template>
                <span>Плейлист</span>
              </v-tooltip>
            </div>
          </div>

        </div>
      </div>
    </v-sheet>


  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AudioPlayer',
  props: {},
  data() {
    return {
      currentAudioIndex: 0,
      durationSeconds: 0,
      speed: 1, // 1.25, 1.5, 1.75, 2
      showPlaylist: false,
      progressElement: document.getElementById('progress'),
      volume: 1,
    };
  },

  computed: {
    ...mapGetters({
      playlist: 'audioPlayer/playlist',
      isPlaying: 'audioPlayer/isPlaying',
      currentSeconds: 'audioPlayer/currentSeconds',
      currentAudio: 'audioPlayer/currentAudio',
      formatTime: 'audioPlayer/formatTime',
      phrases: 'transcribe/phrases',
      call: 'reports_speech_analysis/call',
    }),

    progressPercentageValue() {
      if (this.currentAudio === null) return '0%';

      return (
        ((this.currentSeconds / parseInt(this.currentAudio.duration)) * 100 ||
          0) + '%'
      );
    },

    isDark() {
      return this.$vuetify.theme.isDark
    },

    getVolumeIcon() {
      let icon = 'mdi-volume-'
      if (this.volume >= 0.6) icon += 'high'
      else if (this.volume >= 0.2) icon += 'medium'
      else if (this.volume >= 0.01) icon += 'low'
      else icon += 'off'

      return icon
    },
  },

  watch: {
    progressElement(newVal) {
      console.log('newVal', newVal);
    },

    playlist(newVal, oldVal) {
      if (oldVal !== newVal) {
        this.changeCurrentAudio(new Audio(newVal[this.currentAudioIndex]));
        this.currentAudio.addEventListener('timeupdate', this.updateTimer);

        this.currentAudio.addEventListener('loadeddata', this.loadAudio);
        this.currentAudio.addEventListener('ended', () => {
          if (this.playlist.length > 0) {
            this.skip('forward');
          } else {
            this.currentAudio.pause();
          }
        });
      }
    },

    isPlaying(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          this.currentAudio.play();
        } else {
          this.currentAudio.pause();
        }
      }
    },
  },

  methods: {
    ...mapActions({
      updateTimer: 'audioPlayer/updateTimer',
      changeCurrentAudio: 'audioPlayer/changeCurrentAudio',
      changeIsPlaying: 'audioPlayer/changeIsPlaying',
      changeCurrentSeconds: 'audioPlayer/changeCurrentSeconds',
      clearPlayer: 'audioPlayer/clearPlayer',
    }),

    loadAudio() {
      this.durationSeconds = parseInt(this.currentAudio.duration);
    },

    playSelectAudio(audioIndex) {
      if (this.isPlaying && this.currentAudioIndex === audioIndex) {
        this.pause();

        return;
      }

      if (this.currentAudioIndex !== audioIndex) {
        this.currentAudioIndex = audioIndex;
        this.currentAudio.src = this.playlist[this.currentAudioIndex];
      }

      this.changeIsPlaying();
    },

    pause() {
      this.changeIsPlaying();
      this.currentAudio.pause();
    },

    skip(direction) {
      if (direction === 'forward') {
        this.currentAudioIndex++;
      } else {
        this.currentAudioIndex--;
      }

      if (this.playlist[this.currentAudioIndex] === undefined) {
        if (this.currentAudioIndex < 0) {
          this.currentAudioIndex = this.playlist.length - 1;
        } else {
          this.currentAudioIndex = 0;
        }
      }

      this.currentAudio.src = this.playlist[this.currentAudioIndex];
    },

    seek(e) {
      let el = e.target.getBoundingClientRect();
      let seekPos = (e.clientX - el.left) / el.width;
      const seconds = this.currentAudio.duration * seekPos;
      this.currentAudio.currentTime = parseInt(seconds);
      this.changeCurrentSeconds(seconds);
    },

    toggleShowPlaylist() {
      this.showPlaylist = !this.showPlaylist;
    },

    getPhraseStyle(phrase) {
      const left = `${
        (phrase.start / (this.currentAudio.duration * 1000)) * 100
      }`;
      const end = `${(phrase.end / (this.currentAudio.duration * 1000)) * 100}`;
      const width = end - left;

      return { left: `${left}%`, width: `${width}%` };
    },

    changeSpeed(speed) {
      this.speed = speed;
      this.currentAudio.playbackRate = speed;
    },

    getFilename() {
      let filename = this.call.agent
      filename += '_' + this.currentAudioIndex
      filename += '_' + this.call.phone
      filename += '_' + this.call.queue_agent_time
      filename = filename.replaceAll(' ', '_')
      filename = filename.replaceAll(':', '-')
      return filename
    },

    spaceHandler(ev) {
      // 32 - код ПРОБЕЛА
      if (ev.keyCode !== 32) return
      const activeElTag = document.activeElement.tagName.toLowerCase()
      if (['select', 'input', 'textarea'].includes(activeElTag)) return

      ev.preventDefault()
      this.changeIsPlaying()
      return false
    },

    changeVolume(val) {
      this.currentAudio.volume = val
    },
  },

  mounted() {
    window.addEventListener('keydown', this.spaceHandler)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.spaceHandler)
    this.currentAudio?.pause();
    this.changeIsPlaying(false);
    this.currentAudio.removeEventListener('timeupdate', this.updateTimer);

    this.currentAudio.removeEventListener('loadeddata', this.loadAudio);
    this.currentAudio.removeEventListener('ended', () => {
      this.skip('forward');
    });
    this.clearPlayer();
  },
}
</script>

<style lang="scss" scoped>
.music-player {
  position: fixed;
  inset: auto 0 0 0; //top = auto right = 0 bottom = 0 left = 0
  z-index: 2;
}

.playlist {
  max-width: 400px;
  position: relative;
  margin-left: auto;
  z-index: 1;

  .wrap {
    max-height: 75px;
    overflow: auto;

    .song-wrap {
      height: 35px;
      width: 100%;
      cursor: pointer;
      border-bottom: 1px solid hwb(0 50% 50% / 0.6);

      &:hover {
        background-color: hwb(0 70% 30% / 0.6);
      }


      .song-details {
        font-size: 0.6em;
        line-height: 35px;
        width: 100%;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding-right: 10px;
        padding-left: 10px;

        span {
          line-height: 15px;

          &.play {
            display: inline-block;
            margin-right: 10px;
            border: 1px solid #fff;
            border-radius: 10px;
            height: 17px;
            width: 17px;
            text-align: center;

            i {
              text-align: center;
            }
          }
        }

        .music-bars-gif {
          height: 10px;
        }
      }
    }

    scrollbar-width: 5px;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-track {
      background-color: #ccc;
      -webkit-box-shadow: inset 0 0 6px rgb(145, 145, 145);
      border-radius: 0;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 0;
      background-color: #d2d2d2;
      -webkit-box-shadow: inset 0 0 6px rgba(238, 238, 238, 0.5);
    }
  }
}

.player {
  position: relative;
  z-index: 2;
  width: 100%;
  border-top: 2px solid hsla(0, 0%, 50%, 0.3);

  animation: blink 500ms alternate 2;

  @keyframes blink {
    to {background-color: hsl(123, 38%, 64%);}
  }

  .main-controls {
    display: grid;
    grid-template-columns: 1fr 5fr 1fr;
    align-items: center;
    gap: 20px;
    height: 60px;
    margin-left: 60px;

    .controls {
      display: flex;
      justify-content: space-around;
      align-items: center;
      // font-size: 1.8rem;

      // .play {
      //   $size: 40px;
      //   $border_size: 2px;

      //   text-align: center;
      //   line-height: $size - $border_size;
      //   color: #28a745;

      //   width: $size;
      //   height: $size;
      //   border-radius: 100vmin;
      //   border: $border_size solid;
      // }
    }

    .seek {
      align-self: start;

      .title-and-time {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 8px;
        font-size: 0.8rem;
      }

      .progress-container {
        position: relative;
        height: 10px;
        width: auto;
        display: flex;
        align-items: center;

        .progress {
          background-color: hwb(0 50% 50% / 0.6);
          height: 4px;
          width: 100%;
          margin: 0;
          padding: 0 2px;
          border-radius: 0;
          display: flex;
          align-items: center;

          box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);

          .transparent-seeker-layer {
            width: 100%;
            height: 6px;
            background-color: transparent;
            position: absolute;
            cursor: pointer;
            z-index: 5;
          }

          .bar {
            width: 0;
            background-color: hsl(291, 64%, 42%);
            height: 4px;
            position: absolute;
          }
        }
      }
    }

    .extra-controls {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;
      font-size: 1.5rem;
    }
  }

  .volume {
    .progress-container {
      position: absolute;
      width: 10px;
      height: 150px;
      display: flex;
      align-items: center;
      top: -150px;

      .progress {
        background-color: rgba(0, 0, 0, 0.05);
        width: 4px;
        height: 100%;
        margin: 0;
        padding: 0 2px;
        border-radius: 0;
        display: flex;
        align-items: center;

        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);

        .transparent-seeker-layer {
          width: 100%;
          height: 6px;
          background-color: transparent;
          position: absolute;
          cursor: pointer;
          z-index: 5;
        }

        .bar {
          width: 0;
          background-color: #fff;
          height: 4px;
          position: absolute;
        }
      }
    }
  }

  .progress-handle {
    display: block;
    position: absolute;
    z-index: 6;
    margin-top: 0;
    margin-left: -2px;
    width: 8px;
    height: 8px;
    border-radius: 100%;
    background-color: #fff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
    cursor: pointer;

    &:hover {
      background-color: #000;
    }
  }
}

.agent-speech-bar,
.client-speech-bar {
  width: 0;
  background-color: #fff;
  height: 4px;
  position: absolute;
}

.agent-speech-bar {
  top: -3px;
  background-color: #2e7d32;
}
.client-speech-bar {
  top: 9px;
  background-color: #1565c0;
}

.height-enter-active {
  animation: bounce-in 0.5s;
}

.height-leave-active {
  animation: bounce-in 0.5s reverse;
}

@keyframes bounce-in {
  0% {
    max-height: 0;
  }
  100% {
    max-height: 75px;
  }
}
</style>